import React, { useCallback } from 'react';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import i18n from '../../../i18n';
import { Box, Grid } from '@material-ui/core';

import { SwitchControlField } from 'components/SwitchControl/SwitchControl';
import { Text, TextTypography } from 'components/Text/Text';
import { UserInfo } from 'components/UserInfo/UserInfo';
import { connect, useSelector } from 'react-redux';
import { Form } from 'forms/types';
import { TextAreaControlField } from 'components/TextAreaControl/TextAreaControl';
import { makeStyles } from '@material-ui/core/styles';
import { QuantityWithCustomButtonsControlField } from 'components/QuantityWithCustomButtonsControl/QuantityWithCustomButtonsControl';
import {
    registrationSettingsEnabledSpecificDeliverySelector,
    registrationSettingsPickUpInfoSelector,
} from 'state/registration/selector';
import { RegistrationSlice } from 'state/registration/reducer';
import mergeWith from 'lodash/mergeWith';
import { DialogNew } from '../../components/Dialog/DialogNew';
import { isUIEnabledSelector } from '../../state/app/selectors';
import { HorizontalDivider } from '../../components/HorizontalDivider/HorizontalDivider';

export interface PickUpFormData {
    enabled: boolean;
    comment: string;
    hoursToStore: number;
}

const useStyles = makeStyles(theme => ({
    content: {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
    },
    buttonContainer: {
        paddingBottom: theme.spacing(1),
    },
    label: {
        display: 'flex',
        color: '#243143',
        textAlign: 'left',
    },
    infoContainer: {
        marginLeft: theme.spacing(6.7),
    },
    title: {
        marginTop: theme.spacing(2),
    },
    subTitle: {
        marginTop: theme.spacing(1),
    },
    line: {
        margin: theme.spacing(3, 0, 2, 0),
        // [theme.breakpoints.down(481)]: {
        //     // margin: theme.spacing(3, 0, 2, 0),
        //     marginBottom: theme.spacing(2),
        // },
    },
}));

const DEFAULT_PICKUP_FORM_DATA = { enabled: false, comment: '', hoursToStore: 24 };

let PickUpFormConnected: any = reduxForm({
    form: Form.REGISTRATION_PICKUP_FORM,
    destroyOnUnmount: false,
    enableReinitialize: true,
})((props: any) => {
    const { t } = i18n.useTranslation();
    const classes = useStyles();

    const isUIEnabled = useSelector(isUIEnabledSelector);
    const enabledSpecificDelivery = useSelector(registrationSettingsEnabledSpecificDeliverySelector);

    const { handleSubmit, hoursToStore, enabled, comment, onClose, initialize } = props;
    const pickUpFormData = useSelector(registrationSettingsPickUpInfoSelector);

    const handleClose = useCallback(() => {
        if (pickUpFormData.enabled === null) {
            initialize(DEFAULT_PICKUP_FORM_DATA);
        } else {
            initialize(pickUpFormData);
        }
        onClose();
    }, [pickUpFormData, initialize, onClose]);

    return (
        <DialogNew
            title={t('onboarding:settings:pickup')}
            fullHeight={true}
            onClose={handleClose}
            onSubmit={handleSubmit}
            leftButton={t('common:settings.closeButton')}
            rightButton={t('onboarding:settings:saveButton')}
            isUIEnabled={isUIEnabled}
        >
            <form onSubmit={handleSubmit} className={classes.content}>
                <Box>
                    <Box mb={2}>
                        <Grid container>
                            <Grid item xs={12}>
                                <Field
                                    name="enabled"
                                    disabled={enabledSpecificDelivery}
                                    component={SwitchControlField}
                                    label={
                                        <Text typography={TextTypography.TITLE_ACCENT_CHANGE} className={classes.label}>
                                            {t('onboarding:settings:pickup')}
                                        </Text>
                                    }
                                />
                            </Grid>
                            <div className={classes.infoContainer}>
                                <Grid item xs={12} className={classes.subTitle}>
                                    <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                                        {t('onboarding:settings:pickupCard:pickupPeriod')}
                                    </Text>
                                </Grid>
                                <Grid item xs={12}>
                                    <Text typography={TextTypography.LEAD_STRONG_CHANGE} className={classes.title}>
                                        {t('onboarding:settings:pickupCard:pickupHours')}
                                    </Text>
                                </Grid>
                                <Grid item className={classes.title}>
                                    <Field
                                        name="hoursToStore"
                                        isDisabled={!enabled || enabledSpecificDelivery}
                                        component={QuantityWithCustomButtonsControlField}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Text typography={TextTypography.LEAD_STRONG_CHANGE} className={classes.title}>
                                        {t('onboarding:settings:pickupCard:additionalInfo')}
                                    </Text>
                                </Grid>
                                <Grid item xs={12} className={classes.subTitle}>
                                    <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                                        {t('onboarding:settings:pickupCard:additionalInfoSubtitle')}
                                    </Text>
                                </Grid>
                                <Grid item xs={12} className={classes.title}>
                                    <Field
                                        name="comment"
                                        component={TextAreaControlField}
                                        rows={5}
                                        maxLength={150}
                                        isDisabled={!enabled || enabledSpecificDelivery}
                                        label={t('onboarding:settings:pickupCard:pickupComment')}
                                    />
                                </Grid>
                            </div>
                        </Grid>
                    </Box>
                    <div className={classes.line}>
                        <HorizontalDivider />
                    </div>

                    {enabled && (
                        <div className={classes.infoContainer}>
                            <Box>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Text typography={TextTypography.PARA}>
                                            {t('onboarding:settings:pickupCard:pickUpDetails')}
                                        </Text>
                                    </Grid>
                                    <Grid item xs={12} className={classes.title}>
                                        <UserInfo>
                                            {t('onboarding:settings:pickupCard:pickUpUserInfo', {
                                                count: Number(hoursToStore),
                                            })}
                                        </UserInfo>
                                    </Grid>
                                </Grid>
                            </Box>
                        </div>
                    )}
                </Box>
            </form>
        </DialogNew>
    );
});

const selector = formValueSelector(Form.REGISTRATION_PICKUP_FORM);

PickUpFormConnected = connect((state: RegistrationSlice) => {
    const { enabled, hoursToStore, comment } = selector(state, 'enabled', 'hoursToStore', 'comment');
    const initialValues = mergeWith({}, DEFAULT_PICKUP_FORM_DATA, state.registration.settings.pickup, (o, s) =>
        s === null ? o : s,
    );
    return { enabled, hoursToStore, comment, initialValues: initialValues };
})(PickUpFormConnected);

export const PickUpForm = PickUpFormConnected;
